'use client';

import type { Transition, Variants } from 'motion/react';
import { motion, useAnimation } from 'motion/react';
import type { HTMLAttributes } from 'react';
import { useCallback, useImperativeHandle, useRef } from 'react';

import { cn } from '@/lib/utils';

type PanelLeftCloseIconHandle = {
  startAnimation: () => void;
  stopAnimation: () => void;
};

type PanelLeftCloseIconProps = HTMLAttributes<HTMLSpanElement> & {
  size?: number;
  ref?: React.Ref<PanelLeftCloseIconHandle>;
};

const defaultTransition: Transition = {
  times: [0, 0.4, 1],
  duration: 0.5,
};

const pathVariants: Variants = {
  normal: { x: 0 },
  animate: { x: [0, -1.5, 0] },
};

function PanelLeftCloseIcon({
  onMouseEnter,
  onMouseLeave,
  className,
  size = 28,
  ref,
  ...props
}: PanelLeftCloseIconProps) {
  const controls = useAnimation();
  const isControlledRef = useRef(false);

  useImperativeHandle(ref, () => {
    isControlledRef.current = true;
    return {
      startAnimation: () => controls.start('animate'),
      stopAnimation: () => controls.start('normal'),
    };
  });

  const handleMouseEnter = useCallback(
    (e: React.MouseEvent<HTMLSpanElement>) => {
      if (isControlledRef.current) {
        onMouseEnter?.(e);
      } else {
        controls.start('animate');
      }
    },
    [controls, onMouseEnter],
  );

  const handleMouseLeave = useCallback(
    (e: React.MouseEvent<HTMLSpanElement>) => {
      if (isControlledRef.current) {
        onMouseLeave?.(e);
      } else {
        controls.start('normal');
      }
    },
    [controls, onMouseLeave],
  );

  return (
    // biome-ignore lint/a11y/noNoninteractiveElementInteractions: Hover events are for decorative animation only
    <span
      className={cn('inline-flex items-center justify-center', className)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      role='img'
      {...props}
    >
      <svg
        fill='none'
        height={size}
        stroke='currentColor'
        strokeLinecap='round'
        strokeLinejoin='round'
        strokeWidth='2'
        viewBox='0 0 24 24'
        width={size}
        xmlns='http://www.w3.org/2000/svg'
      >
        <title>Close panel</title>
        <rect height='18' rx='2' width='18' x='3' y='3' />
        <path d='M9 3v18' />
        <motion.path
          animate={controls}
          d='m16 15-3-3 3-3'
          transition={defaultTransition}
          variants={pathVariants}
        />
      </svg>
    </span>
  );
}

PanelLeftCloseIcon.displayName = 'PanelLeftCloseIcon';

export type { PanelLeftCloseIconHandle, PanelLeftCloseIconProps };
export { PanelLeftCloseIcon };
